import React, { useState, useEffect } from 'react';

/**
 * TaskProgressModal — 进度提示弹窗
 *
 * 用于长时间任务（推送、拉取、批量操作）的等待提示。
 * 始终使用无限滚动（indeterminate）进度条，进度信息通过 message 文字展示。
 *
 * Props:
 *   isOpen          - boolean 是否显示
 *   title           - 标题
 *   message         - 当前状态描述（会动态更新）
 *   onClose         - 关闭回调（可让用户手动关闭）
 *   showClose       - 是否显示关闭按钮（默认 false）
 */
export default function TaskProgressModal({
  isOpen,
  title = '',
  message = '',
  onClose,
  showClose = false,
}) {
  const [visible, setVisible] = useState(false);
  const [animPhase, setAnimPhase] = useState('entering');

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setAnimPhase('entering');
      const t = setTimeout(() => setAnimPhase(''), 50);
      return () => clearTimeout(t);
    } else {
      setAnimPhase('exiting');
      const t = setTimeout(() => {
        setVisible(false);
        setAnimPhase('');
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  if (!visible) return null;



  return (
    <div className={`fixed inset-0 z-[10000] flex items-center justify-center transition-opacity duration-300 ${
      animPhase === 'exiting' ? 'opacity-0' : 'opacity-100'
    }`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={showClose ? onClose : undefined} />

      {/* Modal */}
      <div className={`relative w-full max-w-md mx-4 transition-all duration-300 ${
        animPhase === 'entering' ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
      } ${animPhase === 'exiting' ? 'scale-95 opacity-0' : ''}`}>
        {/* Glass card */}
        <div className="relative overflow-hidden rounded-2xl bg-slate-900/90 dark:bg-slate-950/90 border border-slate-700/50 shadow-2xl shadow-blue-500/10">
          {/* Animated gradient border glow */}
          <div className="absolute inset-0 rounded-2xl p-[1px] pointer-events-none">
            <div className="w-full h-full rounded-2xl bg-gradient-to-r from-blue-500/30 via-cyan-400/30 to-violet-500/30 animate-pulse" />
          </div>

          {/* Content */}
          <div className="relative px-6 py-8">
            {/* Title */}
            {title && (
              <h3 className="text-lg font-semibold text-white mb-1 text-center tracking-wide">
                {title}
              </h3>
            )}

            {/* Message */}
            {message && (
              <p className="text-sm text-slate-300 text-center mb-6 leading-relaxed">
                {message}
              </p>
            )}

            {/* Indeterminate progress bar */}
            <div className="relative h-2.5 bg-slate-800/80 rounded-full overflow-hidden border border-slate-700/50">
              {/* Background shimmer */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
              {/* Indeterminate bar - animated sweep */}
              <div className="h-full w-1/2 rounded-full relative animate-sweep"
                style={{
                  background: 'linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6)',
                  boxShadow: '0 0 12px rgba(59, 130, 246, 0.5), 0 0 30px rgba(6, 182, 212, 0.2)',
                }}
              >
                <div className="absolute right-0 top-0 h-full w-6 bg-white/20 blur-md rounded-full" />
              </div>
            </div>

            {/* Animated dots / loading indicator */}
            <div className="flex justify-center gap-1 mt-4">
              {[0, 1, 2].map(i => (
                <div key={i}
                  className="w-1.5 h-1.5 rounded-full bg-blue-400/60 animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s`, animationDuration: '1s' }}
                />
              ))}
            </div>

            {/* Close button */}
            {showClose && onClose && (
              <div className="flex justify-center mt-4">
                <button onClick={onClose}
                  className="px-4 py-1.5 text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors">
                  取消
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Keyframes injection */}
      <style>{`
        @keyframes sweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-sweep {
          animation: sweep 1.8s ease-in-out infinite;
        }
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
